import React, { useState, useRef, useEffect } from 'react';
import { useContactBook } from '@/hooks/useContactBook';
import { ContactBookDropdown } from './ContactBookDropdown';
import { ContactSelectedBadge } from './ContactSelectedBadge';
import { ContactBookProps, ContactBookSelection } from './ContactBookTypes';
import { ContactBookResult } from '@/services/api/contacts';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export const ContactBook = ({
  onContactSelect,
  onPhoneNumberChange,
  mode = 'dialer',
  placeholder = mode === 'dialer' ? 'Search by phone number...' : 'Search by email...',
  className,
  searchValue,
  onSearchChange
}: ContactBookProps) => {
  const [internalSearchValue, setInternalSearchValue] = useState('');
  const [selectedContact, setSelectedContact] = useState<ContactBookSelection | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const { contacts, loading, error, searchContacts, clearResults } = useContactBook();

  // Use external search value if provided, otherwise use internal state
  const currentSearchValue = searchValue !== undefined ? searchValue : internalSearchValue;
  const setCurrentSearchValue = onSearchChange || setInternalSearchValue;

  // Search when value changes
  useEffect(() => {
    if (currentSearchValue.trim()) {
      searchContacts(currentSearchValue);
      setShowDropdown(true);
    } else {
      clearResults();
      setShowDropdown(false);
    }
  }, [currentSearchValue, searchContacts, clearResults]);

  // Hide dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleContactSelect = (contact: ContactBookResult, phone?: string, email?: string) => {
    const selection: ContactBookSelection = {
      contact,
      selectedPhone: phone,
      selectedEmail: email
    };

    setSelectedContact(selection);
    setShowDropdown(false);
    
    // Update phone number for dialer mode
    if (mode === 'dialer' && phone && onPhoneNumberChange) {
      onPhoneNumberChange(phone);
    }

    // Clear search when contact is selected
    setCurrentSearchValue('');
    
    // Notify parent component
    onContactSelect?.(selection);
  };

  const handleClearSelection = () => {
    console.log('🗑️ Clearing contact selection');
    setSelectedContact(null);
    if (mode === 'dialer' && onPhoneNumberChange) {
      console.log('📞 Clearing phone number in dialer mode');
      onPhoneNumberChange('');
    }
    console.log('✅ Contact selection cleared');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCurrentSearchValue(value);
    
    // Clear selected contact when typing new search
    if (selectedContact && value.trim()) {
      setSelectedContact(null);
      if (mode === 'dialer' && onPhoneNumberChange) {
        onPhoneNumberChange('');
      }
    }
  };

  const handleInputFocus = () => {
    if (currentSearchValue.trim() && contacts.length > 0) {
      setShowDropdown(true);
    }
  };

  return (
    <div className={cn("w-full", className)} ref={containerRef}>
      <div className="relative">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
          <Input
            type="text"
            value={currentSearchValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={selectedContact ? "Search for a different contact..." : placeholder}
            className={cn("pl-10", selectedContact && "opacity-0")}
          />
        </div>

        {/* Contact Badge Overlay */}
        {selectedContact && (
          <div className="absolute inset-0 z-20">
            <ContactSelectedBadge
              selection={selectedContact}
              onClear={handleClearSelection}
              mode={mode}
            />
          </div>
        )}

        {/* Search Results Dropdown */}
        <ContactBookDropdown
          contacts={contacts}
          loading={loading}
          error={error}
          onContactSelect={handleContactSelect}
          mode={mode}
          show={showDropdown}
          searchQuery={currentSearchValue}
        />
      </div>
    </div>
  );
};